/**
 * Every /channels/:platform read of spec §2.2, each under its own run key,
 * through D1's useKeyedRead (unchanged). A result read under another key is
 * never shown, and a failed read is 'failed' (logged), never empty. The keys:
 * - facts: scope + channel + both windows (R1: the cut windows are client filters);
 * - entities: scope + channel, not windowed; none on the GA4 tab;
 * - ads: scope + channel + the current window; none on the GA4 tab;
 * - adSpend: scope + the current window (get_overview_facts, R1): on the GA4
 *   tab, and on an ad tab whose current facts spent in more than one known
 *   currency, where D2 opens in D1's currency (rules-04 B4). Keyed after the
 *   facts read, so a single-currency channel reads nothing more;
 * - connections: the workspace alone; none in all workspaces, where the
 *   freshness pill stays idle too (R6: statuses are workspace-bound).
 * One attempt counter sits in every key: retry() re-reads everything once (R10).
 * A null scope reads nothing: every read is idle. That covers a workspace not
 * known yet, and the GA4 tab in all workspaces, where the page passes null (R6).
 *
 * No filter is an input. Objective, currency, campaign type, campaign, Active
 * and the breakdown tab are applied by buildChannelView on the client and read
 * nothing (spec §2.2). Only the scope, the channel, the period and the attempt
 * reach a run key.
 *
 * @module hooks/useChannelData
 */
import { useCallback, useMemo, useState } from 'react';
import { bangkokToday, type DateRange } from '@/services/looker/lookerDateRange';
import { loadChannelConnections, loadChannelFactPair } from '@/services/channels/channelLoaders';
import { readChannelAds, readChannelEntities } from '@/services/channels/channelReaders';
import type {
  ChannelAdRow, ChannelConnections, ChannelEntity, ChannelFactPair, ChannelPlatform,
} from '@/services/channels/channelTypes';
import { readOverviewFacts } from '@/services/overview/overviewFacts';
import { currencyChoiceOf } from '@/services/overview/overviewFilters';
import { overviewWindowsOf } from '@/services/overview/overviewWindows';
import type { FactRow, OverviewWindows, ReadState } from '@/services/overview/overviewTypes';
import type { ChannelFreshnessState } from './useLookerChannelFreshness';
import type { OverviewScope } from './useOverviewData';
import { useKeyedRead } from './useKeyedRead';

export interface ChannelData {
  /** null = no bounded range (an empty "this month", a refused custom range): no windowed read. */
  windows: OverviewWindows | null;
  facts: ReadState<ChannelFactPair>;
  /** idle on the GA4 tab. */
  entities: ReadState<ChannelEntity[]>;
  /** idle on the GA4 tab. */
  ads: ReadState<ChannelAdRow[]>;
  /**
   * get_overview_facts over the current window (every ad channel): the GA4 tab's ad-spend line, and D1's currency on
   * an ad tab whose facts spent in more than one currency (rules-04 B4). Idle otherwise.
   */
  adSpend: ReadState<FactRow[]>;
  /** idle in all workspaces. */
  connections: ReadState<ChannelConnections>;
  /** Mirrors `connections`, so idle in all workspaces. */
  freshness: ChannelFreshnessState;
  /** Re-reads everything once (R10). */
  retry: () => void;
}

interface RunKeys { facts: string; entities: string; ads: string }

const NO_READ: RunKeys = { facts: '', entities: '', ads: '' };

const unscoped = (): Promise<never> => Promise.reject(new Error('channels: no scope'));
const span = (r: DateRange): string => `${r.start}..${r.end}`;
const whereOf = (scope: OverviewScope): string => `${scope.workspaceId ?? 'all'}|${scope.workspaceIds.join(',')}`;

/** The run key of each scope-wide read; '' = idle (useKeyedRead reads nothing). */
const runKeysOf = (
  scope: OverviewScope | null, platform: ChannelPlatform, w: OverviewWindows | null, attempt: number,
): RunKeys => {
  if (!scope) return NO_READ;
  const where = whereOf(scope);
  const at = `a:${attempt}`;
  const ga = platform === 'analytics';
  return {
    // I1 (D1): both windows key the facts: 'mtd' and its equal custom range share `current`, not `previous`.
    facts: w ? `facts|${where}|${platform}|${span(w.current)}|${span(w.previous)}|${at}` : '',
    entities: ga ? '' : `entities|${where}|${platform}|${at}`,
    ads: w && !ga ? `ads|${where}|${platform}|${span(w.current)}|${at}` : '',
  };
};

/**
 * Every ad channel's spend, keyed after the facts read: always on the GA4 tab (its ad-spend line); on an ad tab only
 * when the channel's current facts spent (> 0) in more than one known currency, the one case where D1's currency can
 * change D2's (rules-04 B4). One key for both, so the two tabs share the read. '' = idle.
 */
const adSpendKeyOf = (
  scope: OverviewScope | null, platform: ChannelPlatform, w: OverviewWindows | null, attempt: number, severalCurrencies: boolean,
): string =>
  (scope && w && (platform === 'analytics' || severalCurrencies) ? `adSpend|${whereOf(scope)}|${span(w.current)}|a:${attempt}` : '');

/** One workspace's connections (also the /channels index redirect's read, R6); idle without a workspace. */
export const useChannelConnections = (workspaceId: string | null, attempt: number): ReadState<ChannelConnections> =>
  useKeyedRead<ChannelConnections>(
    workspaceId ? `connections|${workspaceId}|a:${attempt}` : '',
    () => (workspaceId ? loadChannelConnections(workspaceId) : unscoped()),
    'ChannelConnections',
  );

/** The data-date pill's state, mirrored from the connections read. */
const freshnessStateOf = (c: ReadState<ChannelConnections>): ChannelFreshnessState => {
  if (c.status === 'ready') return { status: 'ready', channels: c.data.freshness };
  return c.status === 'failed' ? { status: 'failed' } : c.status === 'loading' ? { status: 'loading' } : { status: 'idle' };
};

export const useChannelData = (scope: OverviewScope | null, platform: ChannelPlatform, token: string): ChannelData => {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const windows = useMemo(() => overviewWindowsOf(token, bangkokToday()), [token]);
  const ids = scope?.workspaceIds ?? [];
  const keys = runKeysOf(scope, platform, windows, attempt);

  const facts = useKeyedRead<ChannelFactPair>(keys.facts,
    () => (windows ? loadChannelFactPair(ids, platform, windows) : unscoped()), 'ChannelFacts');
  const entities = useKeyedRead<ChannelEntity[]>(keys.entities, () => readChannelEntities(ids, platform), 'ChannelEntities');
  const ads = useKeyedRead<ChannelAdRow[]>(keys.ads,
    () => (windows ? readChannelAds(ids, platform, windows.current) : unscoped()), 'ChannelAds');
  const severalCurrencies = useMemo(
    () => facts.status === 'ready' && currencyChoiceOf(facts.data.current, null).currencies.length > 1, [facts],
  );
  const adSpend = useKeyedRead<FactRow[]>(adSpendKeyOf(scope, platform, windows, attempt, severalCurrencies),
    () => (windows ? readOverviewFacts(ids, windows.current) : unscoped()), 'ChannelAdSpend');
  const connections = useChannelConnections(scope?.workspaceId ?? null, attempt);
  const freshness = useMemo(() => freshnessStateOf(connections), [connections]);

  return useMemo(
    () => ({ windows, facts, entities, ads, adSpend, connections, freshness, retry }),
    [windows, facts, entities, ads, adSpend, connections, freshness, retry],
  );
};
